import ContainerNavigationView from "../../common/view/ContainerNavigationView";
import Term, { getNextLevel } from "../../term/Term";
import styles from "../css/LearningResultTermListScreen.module.css";
import { useTranslation } from "react-i18next";

import { ReactComponent as CloseIcon } from "../../../img/iconImgAllClose.svg";
import LearningResultCell from "./LearningResultCell";

import {
  updateTermLevel as updateTermLevelFromAPI,
  updateTermMark as updateTermMarkFromAPI,
  updateTerm as updateTermFromAPI,
  deleteTerm as deleteTermFromAPI,
  updateTermInformation,
} from "../../term/termAPI";

import { useAppDispatch, useAppSelector } from "../../../hook";

import { selectTerms, updateTerms } from "../../term/termSlice";
import {
  TermLevel,
  getCurrentLevelExp,
} from "../../term/custom_view/TermLevel";
import { useEffect, useState } from "react";
import { fetchGroupLanguageCode } from "../../groups/groupAPI";
import { fetchSentence } from "../../sentence/service/sentenceAPI";
import { generateMoreSentence } from "../../sentence/service/moreSentence";
import SentenceHistory from "../../sentence/model/SentenceHistory";
import { QuestionType } from "../../settings/model/QuestionType";
import BasicMenu, { BasicMenuButton } from "../../common/BasicMenu";
import TermEditScreen from "../../term/view/TermEditScreen";
import OnevocaDialog from "../../common/OnevocaDialog";
import VoiceLanguageSelectScreen from "../../voice/view/VoiceLanguageSelectScreen";
import Language from "../../language/Language";
import { setUserCustomLanguage } from "../../sentence/manager/sentenceSettings";
import LoadingScreen from "../../common/LoadingScreen";
import { LearningAnswerType } from "../model/LearningAnswerType";
import SelectableTermListScreen from "../../term/view/SelectableTermListScreen";
import ListEmptyText from "../../common/ListEmptyText";
import DropDownButton from "../../common/view/DropDownButton";
import BigButton from "../../common/BigButton";
import { TermSentenceWithStringValue } from "../../sentence/model/Sentence";
import { updateAndReturnTermMemo } from "../../term/terms";

interface LearningResultTermListScreenProps {
  showing: boolean;
  termList: Term[];
  type: LearningAnswerType;
  onTapRetryButton: (list: Term[], type: LearningAnswerType) => void;
  onUpdateTermList: (list: Term[], type: LearningAnswerType) => void;
  onDeleteTermList: (list: string[], type: LearningAnswerType) => void;
  onClose: () => void;
}

export default function LearningResultTermListScreen(
  props: LearningResultTermListScreenProps
) {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const termsDispatch = useAppDispatch();
  const rawTerms = useAppSelector(selectTerms);

  const [termList, setTermList] = useState<Term[]>([]);
  const [sentenceHistory] = useState(() => new SentenceHistory());
  const [selectedTerm, setSelectedTerm] = useState<Term | null>(null);
  const [selectedCorrectType, setSelectedCorrectType] =
    useState<LearningAnswerType>();

  const titleString =
    selectedCorrectType === LearningAnswerType.correct
      ? t("CorrectAnswer")
      : t("IncorrectAnswer");

  const retryButtonTitle =
    selectedCorrectType === LearningAnswerType.correct
      ? t("RetryCorrectButton")
      : t("RetryIncorrectButton");

  const [showingListEditScreen, setShowingListEditScreen] = useState(false);

  const [isOpenTermCellMoreMenu, setIsOpenTermCellMoreMenu] =
    useState<Term | null>(null);
  const [isOpenCorrectTypeSelectMenu, setIsOpenCorrectTypeSelectMenu] =
    useState(false);
  const [
    isOpenSentenceLanguageSelectScreen,
    setIsOpenSentenceLanguageSelectScreen,
  ] = useState(false);
  const [showingSentenceMoreMenu, setShowingSentenceMoreMenu] =
    useState<TermSentenceWithStringValue>();

  const [isLoading, setIsLoading] = useState(false);

  const [termEditSelectedItem, setTermEditSelectedItem] = useState<Term | null>(
    null
  );
  const [termDeleteSelectedItem, setTermDeleteSelectedItem] =
    useState<Term | null>(null);

  const getTermCellMoreButtons = (): BasicMenuButton[] => [
    {
      title: t("Edit"),
      handleOnClose: () => {
        const term = isOpenTermCellMoreMenu;
        if (term !== null) {
          setTermEditSelectedItem(term);
        }
        setIsOpenTermCellMoreMenu(null);
      },
    },
    {
      title: t("Delete"),
      handleOnClose: () => {
        const term = isOpenTermCellMoreMenu;
        if (term !== null) {
          setTermDeleteSelectedItem(term);
        }
        setIsOpenTermCellMoreMenu(null);
      },
    },
  ];

  const getTermCellSentenceMoreButtons = (): BasicMenuButton[] => [
    {
      title: t("ExampleSaveToTermMemo"),
      handleOnClose: () => {
        const item = showingSentenceMoreMenu;
        if (item !== undefined) {
          updateMemo(item);
        }
        setShowingSentenceMoreMenu(undefined);
      },
    },
  ];

  const getCorrectTypeMenuButtons = (): BasicMenuButton[] => [
    {
      title: t("CorrectAnswer"),
      handleOnClose: () => {
        setSelectedCorrectType(LearningAnswerType.correct);
        setIsOpenCorrectTypeSelectMenu(false);
      },
    },
    {
      title: t("IncorrectAnswer"),
      handleOnClose: () => {
        setSelectedCorrectType(LearningAnswerType.incorrect);
        setIsOpenCorrectTypeSelectMenu(false);
      },
    },
  ];

  useEffect(() => {
    if (!props.showing) return;
    setSelectedCorrectType(props.type);
  }, [props.showing, props.type]);

  useEffect(() => {
    filterTermList(props.termList);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.showing, props.termList, selectedCorrectType]);

  function filterTermList(termList: Term[]) {
    const newTerms = termList.filter((term) =>
      selectedCorrectType === LearningAnswerType.correct
        ? term.isCorrectAnswer
        : !term.isCorrectAnswer
    );
    setTermList(newTerms);
  }

  function handleOnClickContainer(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
  }

  function updateTermMarkToList(term: Term, isOn: boolean) {
    if (selectedCorrectType === undefined) return;

    const terms = rawTerms.map((rawTerm: any) => Term.fromJson(rawTerm));
    const termIndex = terms.findIndex((t) => t.id === term.id);
    if (termIndex !== -1) {
      const newTerms = [...terms];
      newTerms[termIndex] = {
        ...newTerms[termIndex],
        isMarked: isOn,
      };
      const serializedTerms = newTerms.map((term) => Term.toJson(term));
      termsDispatch(updateTerms(serializedTerms));
    }

    const newTermIndex = termList.findIndex((t) => t.id === term.id);
    if (newTermIndex !== -1) {
      const newTermList = [...termList];
      newTermList[newTermIndex] = {
        ...newTermList[newTermIndex],
        isMarked: isOn,
      };
      setTermList(newTermList);
      props.onUpdateTermList(newTermList, selectedCorrectType);
    }
  }

  function updateTermLevelToList(term: Term, toLevel: TermLevel) {
    if (selectedCorrectType === undefined) return;

    const terms = rawTerms.map((rawTerm: any) => Term.fromJson(rawTerm));
    const termIndex = terms.findIndex((t) => t.id === term.id);
    if (termIndex !== -1) {
      const newTerms = [...terms];
      newTerms[termIndex] = {
        ...newTerms[termIndex],
        level: toLevel,
        exp: getCurrentLevelExp(toLevel),
      };
      const serializedTerms = newTerms.map((term) => Term.toJson(term));
      termsDispatch(updateTerms(serializedTerms));
    }

    const newTermIndex = termList.findIndex((t) => t.id === term.id);
    if (newTermIndex !== -1) {
      const newTermList = [...termList];
      newTermList[newTermIndex] = {
        ...newTermList[newTermIndex],
        level: toLevel,
        exp: getCurrentLevelExp(toLevel),
      };
      setTermList(newTermList);
      props.onUpdateTermList(newTermList, selectedCorrectType);
    }
  }

  async function generateSentence(term: Term) {
    setTermList((prevTerms) => {
      const index = prevTerms.findIndex((t) => t.id === term.id);
      if (index === -1) return prevTerms;

      const updated = [...prevTerms];
      updated[index] = {
        ...updated[index],
        isGeneratingSentence: true,
      };
      return updated;
    });

    try {
      const languageCode =
        term.group.length !== 0 ? await fetchGroupLanguageCode(term.group) : "";
      const sentence = await fetchSentence(
        term.term,
        languageCode,
        sentenceHistory
      );

      setTermList((prevTerms) => {
        const index = prevTerms.findIndex((t) => t.id === term.id);
        if (index === -1) return prevTerms;

        const updated = [...prevTerms];
        updated[index] = {
          ...updated[index],
          sentence,
          isGeneratingSentence: false,
        };
        return updated;
      });
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  function deleteSentence(term: Term) {
    const termIndex = termList.findIndex((t) => t.id === term.id);

    if (termIndex !== -1) {
      const newTerms = [...termList];
      const { sentence, ...rest } = newTerms[termIndex];
      const newTerm = { ...rest };

      newTerms[termIndex] = newTerm;

      setTermList(newTerms);
    }
  }

  async function updateMemo(item: TermSentenceWithStringValue) {
    setIsLoading(true);
    try {
      await updateTermInformation(
        item.term.id,
        undefined,
        undefined,
        item.stringValue,
        undefined
      );
      updateTermToList({ ...item.term, memo: item.stringValue });
      updateTermMemoToCurrentList(item.term, item.stringValue);
      setIsLoading(false);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setIsLoading(false);
    }
  }

  function updateTermMemoToCurrentList(term: Term, memo: string) {
    if (selectedCorrectType === undefined) return;

    const newTermList = updateAndReturnTermMemo(termList, term.id, memo);
    setTermList(newTermList);
    props.onUpdateTermList(newTermList, selectedCorrectType);
  }

  async function updateTerm(term: Term) {
    setIsLoading(true);
    try {
      await updateTermFromAPI(term);
      updateTermToList(term);
      setIsLoading(false);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setIsLoading(false);
    }
  }

  async function deleteTerm(term: Term) {
    if (selectedCorrectType === undefined) return;
    setIsLoading(true);
    try {
      await deleteTermFromAPI(term);
      deleteTermToList(term);
      setIsLoading(false);
      props.onDeleteTermList([term.id], selectedCorrectType);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setIsLoading(false);
    }
  }

  function deleteTermToList(term: Term) {
    const terms = rawTerms.map((rawTerm: any) => Term.fromJson(rawTerm));
    const termIndex = terms.findIndex((t) => t.id === term.id);
    if (termIndex !== -1) {
      const newTerms = [...terms];
      newTerms.splice(termIndex, 1);
      const serializedTerms = newTerms.map((term) => Term.toJson(term));
      termsDispatch(updateTerms(serializedTerms));
    }
  }

  function updateTermToList(term: Term) {
    const terms = rawTerms.map((rawTerm: any) => Term.fromJson(rawTerm));
    const termIndex = terms.findIndex((t) => t.id === term.id);
    if (termIndex !== -1) {
      const newTerms = [...terms];
      newTerms[termIndex] = term;
      const serializedTerms = newTerms.map((term) => Term.toJson(term));
      termsDispatch(updateTerms(serializedTerms));
    }
  }

  function updateSentenceLanguage(language: Language) {
    setUserCustomLanguage(language.code);

    if (selectedTerm != null) {
      generateSentence(selectedTerm);
    }
  }

  async function handleOnClickTermLevel(term: Term) {
    try {
      const nextLevel = getNextLevel(term.level);
      updateTermLevelFromAPI(term, nextLevel);
      updateTermLevelToList(term, nextLevel);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  function handleOnClickCorrectTypeButton(
    event: React.MouseEvent<HTMLDivElement>
  ) {
    setAnchorEl(event.currentTarget);
    setIsOpenCorrectTypeSelectMenu(true);
  }

  function handleOnClickGenerateSentenceButton(term: Term) {
    if (term.sentence === undefined) {
      generateSentence(term);
    } else {
      deleteSentence(term);
    }
  }

  function handleOnClickMoreSentenceButton(term: Term) {
    generateMoreSentence(term, setTermList, sentenceHistory);
  }

  function handleOnClickTermCellMoreButton(
    event: React.MouseEvent<HTMLDivElement>,
    term: Term
  ) {
    setAnchorEl(event.currentTarget);
    setIsOpenTermCellMoreMenu(term);
  }

  function handleOnClickSentenceMoreButton(
    event: React.MouseEvent<HTMLDivElement>,
    item: TermSentenceWithStringValue
  ) {
    setAnchorEl(event.currentTarget);
    setShowingSentenceMoreMenu(item);
  }

  async function handleOnClickTermMark(term: Term) {
    try {
      const newMark = !term.isMarked;
      updateTermMarkFromAPI(term, newMark);
      updateTermMarkToList(term, newMark);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  function handleOnClickSentenceLanguageButton(term: Term) {
    setSelectedTerm(term);
    setIsOpenSentenceLanguageSelectScreen(true);
  }

  if (!props.showing) return null;
  return (
    <>
      <div className={styles.stack}>
        <div className={styles.main} onClick={props.onClose}>
          <div className={styles.container} onClick={handleOnClickContainer}>
            <div className={styles.contentContainer}>
              <ContainerNavigationView
                open={true}
                title={titleString}
                titleView={
                  <DropDownButton
                    title={titleString}
                    handleOnClick={handleOnClickCorrectTypeButton}
                  />
                }
                leftImageButtonImage={CloseIcon}
                onClickLeftImageButton={props.onClose}
                rightTextButtonText={t("Edit")}
                onClickRightTextButton={() => {
                  setShowingListEditScreen(true);
                }}
              />

              <div className={styles.listContainer}>
                {termList
                  .sort((a, b) => {
                    if (a.isCorrectAnswer !== b.isCorrectAnswer) {
                      return a.isCorrectAnswer ? 1 : -1;
                    }
                    return a.index! - b.index!;
                  })
                  .map((term) => (
                    <LearningResultCell
                      key={term.id}
                      term={term}
                      config={{ firstShow: QuestionType.termFirst }}
                      handleOnClickMarkButton={() =>
                        handleOnClickTermMark(term)
                      }
                      handleOnClickLevelButton={() =>
                        handleOnClickTermLevel(term)
                      }
                      handleOnClickMoreButton={(event) =>
                        handleOnClickTermCellMoreButton(event, term)
                      }
                      handleOnClickGenerateSentenceButton={() =>
                        handleOnClickGenerateSentenceButton(term)
                      }
                      handleOnClickSentenceLanguageButton={() =>
                        handleOnClickSentenceLanguageButton(term)
                      }
                      handleOnClickMoreSentenceButton={() =>
                        handleOnClickMoreSentenceButton(term)
                      }
                      handleOnClickSentenceMoreButton={
                        handleOnClickSentenceMoreButton
                      }
                    />
                  ))}
              </div>

              {termList.length === 0 && (
                <div className={styles.termsEmptyTextContainer}>
                  <ListEmptyText text={t("NoTermsText")} />
                </div>
              )}
            </div>

            <BigButton
              title={retryButtonTitle}
              variant={termList.length === 0 ? "disable" : "normal"}
              handleClick={() => {
                if (selectedCorrectType === undefined || termList.length === 0)
                  return;
                props.onTapRetryButton(termList, selectedCorrectType);
              }}
            />
          </div>
        </div>

        <BasicMenu
          isOpen={isOpenTermCellMoreMenu !== null}
          anchorEl={anchorEl}
          buttons={getTermCellMoreButtons()}
          handleOnClose={() => {
            setIsOpenTermCellMoreMenu(null);
          }}
        />

        <BasicMenu
          isOpen={isOpenCorrectTypeSelectMenu}
          anchorEl={anchorEl}
          buttons={getCorrectTypeMenuButtons()}
          handleOnClose={() => {
            setIsOpenCorrectTypeSelectMenu(false);
          }}
        />

        <BasicMenu
          isOpen={showingSentenceMoreMenu !== undefined}
          anchorEl={anchorEl}
          buttons={getTermCellSentenceMoreButtons()}
          handleOnClose={() => setShowingSentenceMoreMenu(undefined)}
        />

        <TermEditScreen
          open={termEditSelectedItem !== null}
          term={termEditSelectedItem!}
          handleOnSave={(term: Term) => updateTerm(term)}
          handleOnClose={() => {
            setTermEditSelectedItem(null);
          }}
        />

        <OnevocaDialog
          open={termDeleteSelectedItem !== null}
          title={null}
          message={t("areyousuremesssage")}
          buttons={[
            {
              title: t("Delete"),
              style: "solid",
              onClick: () => {
                const term = termDeleteSelectedItem;
                if (term !== null) {
                  setTermDeleteSelectedItem(null);
                  deleteTerm(term);
                }
              },
            },
            {
              title: t("Cancel"),
              style: "normal",
              onClick: () => {
                setTermDeleteSelectedItem(null);
              },
            },
          ]}
        />

        <VoiceLanguageSelectScreen
          open={isOpenSentenceLanguageSelectScreen === true}
          handleOnClickLanguage={updateSentenceLanguage}
          handleOnClose={() => {
            setIsOpenSentenceLanguageSelectScreen(false);
          }}
        />

        <SelectableTermListScreen
          showing={showingListEditScreen}
          termList={termList}
          onUpdateTermList={(updatedTermList: Term[]) => {
            if (selectedCorrectType === undefined) return;
            props.onUpdateTermList(updatedTermList, selectedCorrectType);
          }}
          onDeleteTermList={(deletedTermIds: string[]) => {
            if (selectedCorrectType === undefined) return;
            props.onDeleteTermList(deletedTermIds, selectedCorrectType);
          }}
          onClose={() => {
            setShowingListEditScreen(false);
          }}
        />

        {isLoading === true && <LoadingScreen />}
      </div>
    </>
  );
}
